import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { CheckCircle2, Sparkles, MapPin, Banknote, Coins } from "lucide-react";
import { PageHeader, KpiCard, SectionCard, RagBadge } from "@/components/Shared";
import SouthAfricaMap from "@/components/SouthAfricaMap";
import ReadinessScore from "@/components/ReadinessScore";
import {
  mineMeta,
  kpiSummary,
  productionTrend,
  revenueTrend,
  equipmentHealth,
  keyHighlights,
  investmentSnapshot,
} from "@/data/mineData";
import { assetTotals, costStructureTotals } from "@/data/financialData";
import { rehabilitation, esgScores } from "@/data/geoEsgData";

const tooltipStyle = {
  contentStyle: {
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 12,
    boxShadow: "0 4px 12px rgba(15,23,42,0.06)",
  },
  labelStyle: { color: "#0f172a", fontWeight: 600 },
};

export default function ExecutiveOverview() {
  return (
    <div data-testid="page-overview">
      <PageHeader
        eyebrow="Executive Overview"
        title={mineMeta.name}
        subtitle={`${mineMeta.location} · ${mineMeta.commodity}`}
      >
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          {mineMeta.status}
        </span>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-production" label="Annual Production" value={kpiSummary.annualProduction.value} unit={kpiSummary.annualProduction.unit} delta={kpiSummary.annualProduction.delta} />
        <KpiCard testid="kpi-revenue" label="Estimated Revenue" value={kpiSummary.estimatedRevenue.value} unit={kpiSummary.estimatedRevenue.unit} delta={kpiSummary.estimatedRevenue.delta} accent="amber" />
        <KpiCard testid="kpi-equipment" label="Equipment Availability" value={kpiSummary.equipmentAvailability.value} unit={kpiSummary.equipmentAvailability.unit} delta={kpiSummary.equipmentAvailability.delta} />
        <KpiCard testid="kpi-reserve" label="Reserve Life" value={kpiSummary.reserveLife.value} unit={kpiSummary.reserveLife.unit} delta={kpiSummary.reserveLife.delta} />
      </div>

      {/* Financial intelligence snapshot */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="exec-financial-snapshot">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-5 text-white relative overflow-hidden">
          <div className="absolute right-4 top-4 opacity-30">
            <Banknote size={48} className="text-amber-400" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-semibold">
            Asset Value Snapshot
          </div>
          <div className="font-display text-3xl font-bold mt-2" data-testid="snapshot-asset-value">
            R{assetTotals.totalBookValue.toLocaleString()}m <span className="text-sm text-slate-300 font-normal">book value</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Replacement value R{assetTotals.totalReplacement.toLocaleString()}m · accumulated depreciation R{assetTotals.totalDepreciation.toLocaleString()}m
          </div>
        </div>
        <div className="bg-gradient-to-r from-amber-700 to-amber-800 rounded-lg p-5 text-white relative overflow-hidden">
          <div className="absolute right-4 top-4 opacity-30">
            <Coins size={48} className="text-yellow-200" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-yellow-200 font-semibold">
            Daily Operating Cost
          </div>
          <div className="font-display text-3xl font-bold mt-2" data-testid="snapshot-daily-cost">
            R{costStructureTotals.daily.toFixed(1)}m <span className="text-sm text-amber-100 font-normal">/ day</span>
          </div>
          <div className="text-xs text-amber-100 mt-1">
            ≈ R{Math.round(costStructureTotals.monthly).toLocaleString()}m / month · R{(costStructureTotals.annual / 1000).toFixed(2)}bn / yr
          </div>
        </div>
      </div>

      {/* Mine sustainability & readiness — strategic header */}
      <div className="mb-6" data-testid="exec-readiness">
        <ReadinessScore />
      </div>

      {/* ESG / Rehab snapshot strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="exec-esg-snapshot">
        <KpiCard testid="exec-esg-composite" label="ESG Composite" value={esgScores.composite.toString()} unit={`E ${esgScores.environmental} · S ${esgScores.social} · G ${esgScores.governance}`} accent="green" />
        <KpiCard testid="exec-rehab-progress" label="Rehab Progress" value={`${rehabilitation.progressPct.toFixed(0)}%`} unit={`${rehabilitation.rehabilitatedHa.toLocaleString()} / ${rehabilitation.disturbedHa.toLocaleString()} ha`} accent="amber" />
        <KpiCard testid="exec-closure-readiness" label="Closure Readiness" value={`${rehabilitation.closureReadinessPct}%`} unit="DMRE-aligned" />
        <KpiCard testid="exec-closure-cover" label="Closure Cover" value={`R${(rehabilitation.liabilityProvisionRm + rehabilitation.rehabGuaranteeRm + rehabilitation.waterComplianceProvisionRm)}m`} unit="provisions + guarantees" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard
          title="Area of Interest"
          description="Asset footprint — Tzaneen, Limpopo Province"
          action={<MapPin size={16} className="text-amber-700" />}
          className="xl:col-span-2"
        >
          <div className="h-[420px]" data-testid="sa-map-container">
            <SouthAfricaMap />
          </div>
        </SectionCard>

        <div className="space-y-4">
          <SectionCard title="Equipment Health" description="Fleet status distribution">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={equipmentHealth} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={3}>
                    {equipmentHealth.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip {...tooltipStyle} />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <SectionCard title="Risk Posture" description="Composite RAG indicator">
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-20 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3.5" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#D97706" strokeWidth="3.5" strokeDasharray="46 100" strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-display text-xl font-bold text-amber-700">4.6</div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">/ 10</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <RagBadge level="amber" />
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                  Moderate — environmental renewal + crusher headroom.
                </p>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard title="Production Trend" description="Tons / month vs target — last 12 months" className="xl:col-span-2">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={productionTrend} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="production" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 3 }} name="Production" />
                <Line type="monotone" dataKey="target" stroke="#B45309" strokeWidth={2} strokeDasharray="4 4" dot={false} name="Target" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Investment Snapshot">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {Object.entries(investmentSnapshot).map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-100 rounded-md px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {k.replace(/([A-Z])/g, " $1").trim()}
                </div>
                <div className="font-display text-lg font-semibold text-slate-900 mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard title="Revenue Trend" description="ZAR millions per quarter, with EBITDA" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueTrend} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F172A" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="ebitda" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B45309" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#B45309" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="quarter" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R${v}m`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `R${v.toLocaleString()}m`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#0F172A" fill="url(#rev)" strokeWidth={2.5} name="Revenue" />
                <Area type="monotone" dataKey="ebitda" stroke="#B45309" fill="url(#ebitda)" strokeWidth={2} name="EBITDA" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Key Highlights" description="What buyers should notice first" action={<Sparkles size={16} className="text-amber-600" />}>
          <ul className="space-y-2.5">
            {keyHighlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`highlight-${i}`}>
                <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}
